import React from 'react';
import PropTypes from 'prop-types';
import {cardLoadMode, cardStyles} from '../const/VuCardConst';
import * as CardUtils from '../utils/CardUtils';
import moment from 'moment';
import _ from 'lodash';
import autobind from 'autobind-decorator';
import ProgramApi from '../api/ProgramApi';

export default class VuCardVuPage extends React.Component {
  state = {
    dates: {},
    showMore: false,
    maxTextLength: 0,
    airings: []
  }

  constructor(props) {
    super(props);
    this.summary = React.createRef();
    this.resizeTimeout = null;
  }

  componentDidMount() {
    this.adjustSummary();
    window.addEventListener('resize', this.onResize);
    this.fetchAirings();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  @autobind
  async fetchAirings() {
    const {record, record: {program}} = this.props;
    try {
      const airings = await ProgramApi.checkAirings(program.id, record.affiliateCallSign, record.id);
      this.setState({airings});
    } catch (e) {
      console.log(e);
    }
  }

  @autobind
  adjustSummary() {
    if (this.summary) {
      const summaryElem = this.summary.current;
      const charWidth = parseFloat(window.getComputedStyle(summaryElem, null).getPropertyValue('font-size')) || 18;
      this.setState({maxTextLength: Math.floor((summaryElem.offsetWidth * 2 / (charWidth * 0.46))) - 6});
    }
  }

  @autobind
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(this.adjustSummary, 250);
  }

  getNetworkLogo() {
    // 1st check xfinity overrides
    const {
      record: {
        program: {
          networkImageLink,
          networkImageAltName,
        }
      }
    } = this.props;
    if (networkImageLink) {
      return {url: networkImageLink, name: networkImageAltName};
    }

    const {record} = this.props;
    const {logoUrl, name} = record.network || {};
    return {url: logoUrl, name};
  }

  render() {
    const {record} = this.props;

    if (!record) {
      return null;
    }

    const {airings} = this.state;
    const {cast, title, fullSummary, pricingInfo} = record;
    const {url: networkLogoUrl, name: networkName} = this.getNetworkLogo();
    const posterUrl = CardUtils.getHorizontalPosterImageUrl(record);

    return (
      <div className="vuCard__content">
        <div
          className="vuCard__content__background"
          style={
            posterUrl && {
              backgroundImage: `url(${posterUrl})`
            }
          }
        >
          <div className="vuCard__content__background__overlay"/>
        </div>
        <div className="vuCard__content__content-container">
          <div className="vuCard__content__network-logo-container">
            {networkLogoUrl && (
              <img className="vuCard__content__network-logo" src={networkLogoUrl} alt={networkName}/>
            )}
          </div>
          <h1 className="vuCard__content__title">{title}</h1>
          {
            pricingInfo &&
            <p className="vuCard__content__pricingInfo">{pricingInfo}</p>
          }
          <p className="vuCard__content__availability">{this.renderNextEpisodeDate()}</p>
          <p className="vuCard__content__show-cast">
            {
              cast.slice(0, 3).map((member, index) => (
                <span className="vuCard__content__show-cast__member" key={`cast-${index}`}>
                  {member}
                </span>
              ))
            }
          </p>
          {
            this.renderSummary(fullSummary)
          }
          {
            this.renderAdditionalFields()
          }
          {
            !_.isEmpty(airings) &&
            <div className="vuCard__content__episodes">
              <div className="vuCard__content__episodes__upcoming-title">Upcoming</div>
              <div className="vuCard__content__episodes__divider"/>
              {
                this.renderAirings()
              }
            </div>
          }
        </div>
      </div>
    );
  }

  renderAirings() {
    let {airings} = this.state;
    const result = [];

    if (_.isEmpty(airings)) {
      return null;
    }

    const uniquePredicate = airing => `${airing.startTime}_${_.get(airing, 'program.episodeTitle')}_${_.get(airing, 'station.channel')}}`;
    airings = _(airings)
      .uniqBy(uniquePredicate)
      .orderBy(['startTime'])
      .map(airing => ({
        ...airing,
        airDate: moment(airing.startTime).format('YYYY-MM-DD')
      }))
      .value();

    const grouped = _.groupBy(airings, 'airDate');
    _.forOwn(grouped, (dayAirings, airDate) => {
      result.push(this.renderDate(airDate, dayAirings.map(airing => this.renderAiring(airing))));
    });

    return result;
  }

  renderDate(date, airings) {
    const {dates} = this.state;
    const isExpanded = dates[date] && dates[date].expanded;
    return (
      <div
        key={`date-${date}`}
        className="vuCard__content__episodes__date"
      >
        <i className={`fa fa-caret-${isExpanded ? 'down' : 'right'}`}/>
        <button
          className="vuCard__content__episodes__date__collapsible"
          onClick={() => this.toggleExpandedDate(date)}
        >
          {this.renderCollapsibleDate(date)}
        </button>
        {
          isExpanded &&
          <div className="vuCard__content__episodes__date__content">
            {airings}
          </div>
        }
      </div>
    );
  }

  renderAiring(airing) {
    const {program: {episodeTitle}, startTime, endTime} = airing;
    const channel = this.getAiringChannel(airing);
    return (
      <div key={`airing-${episodeTitle}-${startTime}-${channel}`} className="vuCard__content__episode">
        <div className="vuCard__content__episode__name">{episodeTitle}</div>
        {this.isAiringLive(airing) && (
          <img src="/static/icons/live.svg" alt="live" className="vuCard__content__episode__type"/>
        )}
        {`Airs ${this.renderStartDate(startTime)} - ${this.renderEndDate(endTime)}`}
        {' on '}
        {channel}
      </div>
    );
  }

  isAiringLive(airing) {
    return _.get(airing, 'qualifiers', []).includes('Live');
  }

  getAiringChannel(airing) {
    const callSign = _.get(airing, 'station.callSign') || _.get(airing, 'station.affiliateCallSign');
    return `${_.get(airing, 'station.channel')} ${callSign}`;
  }

  renderCollapsibleDate(date) {
    return moment(date).format('dddd, MMMM D');
  }

  renderStartDate(startTime) {
    return moment(startTime).format('ddd h:mm');
  }

  renderEndDate(endTime) {
    return moment(endTime).format('h:mma');
  }

  renderNextEpisodeDate() {
    let {airings} = this.state;

    if (_.isEmpty(airings)) {
      return null;
    }

    airings = _.orderBy(airings, ['startTime']);
    const {startTime} = airings[0];
    const date = moment(startTime).format('h:mma');
    return `${this.isAiringLive(airings[0]) ? 'Live' : 'Available'} ${date} on ${this.getAiringChannel(airings[0])}`;
  }

  toggleExpandedDate(date) {
    const {dates} = this.state;
    const newDates = date in dates ? dates : {...dates, [date]: {}};
    newDates[date].expanded = !newDates[date].expanded;
    this.setState({dates: newDates});
  }

  renderSummary(fullSummary) {
    const {showMore, maxTextLength} = this.state;
    let displayedText = fullSummary;
    let styleProps = {};
    const tooLong = fullSummary && fullSummary.length > maxTextLength;

    if (maxTextLength > 0) {
      if (tooLong && !showMore) {
        displayedText = fullSummary.substring(0, maxTextLength);
      }
    } else {
      styleProps = !maxTextLength ? {style: {visibility: 'hidden'}} : {};
    }

    const moreLessLink = (maxTextLength > 0 && tooLong) ?
      (
        showMore ?
          <span>&nbsp;<a onClick={this.showLess}>less</a></span>
          :
          <span>&hellip;&nbsp;<a onClick={this.showMore}>more</a></span>
      ) : null;

    return (
      <p className="vuCard__content__show-description" ref={this.summary} {...styleProps}>
        {displayedText}
        {moreLessLink}
      </p>
    );
  }

  renderAdditionalFields() {
    const {
      record: {
        program: {
          commonSenseShorthand,
          duration,
          guidanceRating,
        }
      }
    } = this.props;

    return (
      <p className="vuCard__content__showFields">
        <div className="vuCard__content__showFields__line1">
          {
            duration &&
            <span className="vuCard__content__showFields__line1__duration">
                {duration}
              </span>
          }
          {
            commonSenseShorthand &&
            <span className="vuCard__content__showFields__line1__commonSenseShorthand">
              <span className="vuCard__content__showFields__line1__commonSenseShorthand__icon"/>
              {commonSenseShorthand}
              </span>
          }
        </div>
        <div className="vuCard__content__showFields__line2">
          {
            guidanceRating &&
            <span className="vuCard__content__showFields__line2__guidanceRating">
              {guidanceRating === 'PG' ? <img src="/static/vupage/PG_icon.svg"/> : guidanceRating}
              </span>
          }
        </div>
        <div className="vuCard__content__showFields__rottenTomatoes">
          {
            this.renderRottenTomatoes()
          }
        </div>
      </p>
    );
  }

  renderRottenTomatoes() {
    const {
      record: {
        program: {
          rottenTomatoesCriticalRanking,
          rottenTomatoesFanRanking,
          rottenTomatoesLink
        }
      }
    } = this.props;

    if (rottenTomatoesCriticalRanking || rottenTomatoesFanRanking) {
      const rankings = (
        <span>
          {
            rottenTomatoesCriticalRanking &&
            <span className="vuCard__content__showFields__rottenTomatoes__criticRating">
              <span className="vuCard__content__showFields__rottenTomatoes__criticRating__icon"/>
              {rottenTomatoesCriticalRanking}
            </span>
          }
          {
            rottenTomatoesFanRanking &&
            <span className="vuCard__content__showFields__rottenTomatoes__fanRating">
              <span className="vuCard__content__showFields__rottenTomatoes__fanRating__icon"/>
              {rottenTomatoesFanRanking}
            </span>
          }
        </span>
      );
      if (rottenTomatoesLink) {
        return (
          <a href={rottenTomatoesLink} className="vuCard__content__showFields__rottenTomatoes__link">
            {rankings}
          </a>
        );
      }
      return rankings;
    }
  }

  @autobind
  showLess() {
    this.setState({showMore: false});
  }

  @autobind
  showMore() {
    this.setState({showMore: true});
  }
}

VuCardVuPage.propTypes = {
  shareUrl: PropTypes.string,
  record: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(Object.values(cardLoadMode)),
  rendererId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderer: PropTypes.object,
  isPreloaded: PropTypes.bool.isRequired
};

VuCardVuPage.defaultProps = {
  shareUrl: '',
  cardStyle: cardStyles.classic,
  isPreloaded: false
};
